import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styles from 'components/Nav/nav.module.scss'
import clsx from 'clsx'
import { Container, Icon, Tooltip, Typography } from 'gu-ui-kit'
import config from 'components/Nav/config'
import { NavLink } from 'react-router-dom'
import Nav from 'components/Nav'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import { findMatchedNavRoutes } from 'utils/findMatchedRoutes'
import { matchPath, useLocation } from 'react-router'
import { SET_DRAWER_STATE } from 'modules/App/types'
import { useDispatch } from 'react-redux'
import { preloadDomainForPathname } from 'store/domains/utils'

const FLYOUT_CLOSE_DURATION_MS = 160

const SubLevel = ({
  label,
  label_key,
  exact,
  path,
  icon,
  routes = [],
  level,
  routeKey = '',
  collapse,
  isFirst = false,
}) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const pathGen = useGeneratePathI18n()
  const displayLabel = label ?? t(label_key)
  const location = useLocation()
  const to = Array.isArray(path) ? path[0] : path
  const resolvedPath = path ? pathGen(to) : null
  const matchedDescendants = useMemo(
    () => findMatchedNavRoutes(routes, location.pathname),
    [routes, location.pathname],
  )
  const selfMatched = useMemo(
    () =>
      resolvedPath ? matchPath(location.pathname, { path: resolvedPath, exact }) : null,
    [resolvedPath, location.pathname, exact],
  )
  const isBranchActive = matchedDescendants.length > 0 || Boolean(selfMatched)
  const [open, setOpen] = useState(routes.length > 0 && isBranchActive)
  const [flyoutVisible, setFlyoutVisible] = useState(false)
  const [flyoutClosing, setFlyoutClosing] = useState(false)
  const [flyoutPosition, setFlyoutPosition] = useState({ top: 0, left: 0 })
  const itemRef = useRef(null)
  const closeFlyoutTimerRef = useRef(null)
  const showFlyout = collapse && routes.length > 0
  const isFlyoutActive = flyoutVisible || flyoutClosing

  const updateFlyoutPosition = useCallback(() => {
    if (!itemRef.current) {
      return
    }

    const rect = itemRef.current.getBoundingClientRect()
    setFlyoutPosition({
      top: rect.top,
      left: rect.right + 8,
    })
  }, [])

  const clearCloseFlyoutTimer = useCallback(() => {
    if (closeFlyoutTimerRef.current) {
      clearTimeout(closeFlyoutTimerRef.current)
      closeFlyoutTimerRef.current = null
    }
  }, [])

  const openFlyoutPanel = useCallback(() => {
    clearCloseFlyoutTimer()
    setFlyoutClosing(false)
    updateFlyoutPosition()
    setFlyoutVisible(true)
  }, [clearCloseFlyoutTimer, updateFlyoutPosition])

  const closeFlyoutPanel = useCallback(() => {
    clearCloseFlyoutTimer()
    setFlyoutClosing(true)
    closeFlyoutTimerRef.current = setTimeout(() => {
      setFlyoutVisible(false)
      setFlyoutClosing(false)
      closeFlyoutTimerRef.current = null
    }, FLYOUT_CLOSE_DURATION_MS)
  }, [clearCloseFlyoutTimer])

  useEffect(() => () => clearCloseFlyoutTimer(), [clearCloseFlyoutTimer])

  useEffect(() => {
    if (!showFlyout) {
      clearCloseFlyoutTimer()
      setFlyoutVisible(false)
      setFlyoutClosing(false)
    }
  }, [clearCloseFlyoutTimer, showFlyout])

  useEffect(() => {
    if (!showFlyout || !isFlyoutActive) {
      return undefined
    }

    const handleReposition = () => updateFlyoutPosition()
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeFlyoutPanel()
      }
    }

    window.addEventListener('resize', handleReposition)
    window.addEventListener('scroll', handleReposition, true)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('resize', handleReposition)
      window.removeEventListener('scroll', handleReposition, true)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [closeFlyoutPanel, isFlyoutActive, showFlyout, updateFlyoutPosition])

  const handleFlyoutMouseEnter = useCallback(() => {
    if (showFlyout) {
      openFlyoutPanel()
    }
  }, [openFlyoutPanel, showFlyout])

  const handleFlyoutMouseLeave = useCallback(() => {
    if (showFlyout) {
      closeFlyoutPanel()
    }
  }, [closeFlyoutPanel, showFlyout])

  const LinkComponent = useMemo(() => {
    if (config?.[routeKey]) {
      return config?.[routeKey]
    }

    if (routes?.length > 0) {
      return 'div'
    }

    return NavLink
  }, [routes, routeKey])

  useEffect(() => {
    if (routes.length > 0) {
      setOpen((prevOpen) => (prevOpen === isBranchActive ? prevOpen : isBranchActive))
    }
  }, [location.pathname, routes.length, isBranchActive])

  const link = (
    <LinkComponent
      key={routeKey}
      className={clsx(styles['nav__link'], 'nav__link_sublevel', {
        [styles['nav__link_first']]: isFirst,
        //eslint-disable-next-line
        [styles['nav__link_opener']]: routes?.length > 0 && !showFlyout,
        ['opened']: open && !showFlyout,
        ['nav__link_opener']: routes?.length > 0 && !showFlyout,
        [styles['nav__link_active']]:
          routes?.length > 0 && ((!open && !showFlyout) || showFlyout) && isBranchActive,
      })}
      onMouseEnter={() => {
        if (path) {
          preloadDomainForPathname(pathGen(to), routeKey)
        }
      }}
      onClick={() => {
        //eslint-disable-next-line
        if (routes?.length > 0) {
          if (showFlyout) {
            return false
          }

          dispatch({ type: SET_DRAWER_STATE, payload: { collapsed: false } })
          setOpen((open) => !open)
          return false
        }

        return true
      }}
      {...(LinkComponent !== 'div'
        ? { activeClassName: styles['nav__link_active'], exact, to: pathGen(to) }
        : {})}
    >
      {icon && (
        <span className={clsx(styles['nav__link-part'], styles['nav__icon'])}>
          <Icon
            name={icon}
            size={'sm'}
          />
        </span>
      )}
      <Typography
        variant={'Body-15'}
        className={clsx(styles['nav__link-part'], styles['nav__text'])}
        noMargin
      >
        {displayLabel}
      </Typography>

      {
        //eslint-disable-next-line
        routes?.length > 0 && !showFlyout && (
          <span className={clsx(styles['nav__link-part'], styles['nav__link_opener'])}>
            <Icon
              className={clsx(styles['nav__link_opener-icon'], {
                [styles['nav__link_opener-icon_open']]: open,
              })}
              name={'arrow-down'}
              size={'sm'}
            />
          </span>
        )
      }
    </LinkComponent>
  )

  const showTooltip = collapse && routes.length === 0

  return (
    <div
      ref={itemRef}
      className={clsx(styles['nav__item'], {
        [styles['nav__item_flyout']]: showFlyout,
        [styles['nav__item_flyout_visible']]: showFlyout && flyoutVisible && !flyoutClosing,
      })}
      onMouseEnter={handleFlyoutMouseEnter}
      onMouseLeave={handleFlyoutMouseLeave}
    >
      {showTooltip ? (
        <Tooltip
          title={displayLabel}
          placement={'right'}
        >
          {link}
        </Tooltip>
      ) : (
        link
      )}
      {
        //eslint-disable-next-line
        routes?.length > 0 && !showFlyout && (
          <Container
            display={'flex'}
            className={clsx(styles['collapse-wrapper'], {
              ['opened']: open,
            })}
            onFocus={() => {
              if (!open) {
                setOpen(true)
              }
            }}
          >
            <div>
              <Nav
                collapse={collapse}
                items={routes}
                level={level + 1}
              />
            </div>
          </Container>
        )
      }
      {
        //eslint-disable-next-line
        routes?.length > 0 && showFlyout && isFlyoutActive && (
          <Container
            display={'flex'}
            className={clsx(styles['collapse-wrapper'], styles['collapse-wrapper_flyout'], {
              [styles['collapse-wrapper_flyout_visible']]: flyoutVisible && !flyoutClosing,
              [styles['collapse-wrapper_flyout_closing']]: flyoutClosing,
            })}
            style={{
              top: flyoutPosition.top,
              left: flyoutPosition.left,
            }}
          >
            <Typography
              variant={'Subtitle-15'}
              color={'Label-Secondary'}
              className={styles['collapse-wrapper_flyout__header']}
              noMargin
            >
              {displayLabel}
            </Typography>
            <Nav
              collapse={false}
              items={routes}
              level={level + 1}
            />
          </Container>
        )
      }
    </div>
  )
}

export default SubLevel

SubLevel.propTypes = {
  collapse: PropTypes.bool,
  icon: PropTypes.oneOfType([PropTypes.oneOf([null]), PropTypes.string]),
  path: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  exact: PropTypes.bool,
  routeKey: PropTypes.string,
  label: PropTypes.string,
  label_key: PropTypes.string,
  routes: PropTypes.arrayOf(PropTypes.object),
  level: PropTypes.number.isRequired,
  isFirst: PropTypes.bool,
  openedGroup: PropTypes.string,
  setOpenedGroup: PropTypes.func,
}
